import type {PinDetailsData} from './types';

import {CloseOutlined, LeftOutlined} from '@ant-design/icons';
import {dateToReadableDate} from '@date-picker';
import {BottomSheet, Divider, IconButton, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import stateEmptyState from 'assets/images/promotion-center/new/stateEmptyState.svg';
import Empty from 'common/empty/Empty';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {getPin} from 'store/slices/pin/thunk';

import PinInfoRow from '../components/common/PinInfoRow/PinInfoRow';
import {PIN_STATE_LABELS} from '../constants';
import {getPinVendorId} from '../utils';
import PackageSummary from './components/PackageSummary/PackageSummary';
import TransactionDetails from './components/TransactionDetails/TransactionDetails';

const PinDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const vendorId = getPinVendorId();
  const {pinId} = useParams();
  const {loading} = useAppSelector((state) => state.pin);
  const [data, setData] = useState<PinDetailsData>();
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);
  const [loadedPinId, setLoadedPinId] = useState<string>();

  useEffect(() => {
    if (!pinId) {
      return;
    }

    let cancelled = false;
    setLoadedPinId(undefined);
    setData(undefined);
    void dispatch(getPin({vendor_id: vendorId, pin_id: pinId}))
      .unwrap()
      .then((response) => {
        if (!cancelled) {
          setData((response?.data ?? {}) as PinDetailsData);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadedPinId(pinId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, pinId, vendorId]);

  if (loading || loadedPinId !== pinId) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  if (!data?.pin) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center px-4 text-center">
        <p className="text-body-sm text-gray-08">اطلاعات بسته برای نمایش در دسترس نیست.</p>
      </div>
    );
  }

  return (
    <PageLayout>
      <section>
        <PinInfoRow
          title="وضعیت"
          value={data.pin.state ? (PIN_STATE_LABELS[data.pin.state] ?? data.pin.state) : ''}
        />
        <PinInfoRow
          title="روز"
          value={
            data.pin.active_date
              ? dateToReadableDate({
                  date: data.pin.active_date,
                  day: true,
                  month: true,
                  year: true
                })
              : ''
          }
        />
        <PinInfoRow title="منطقه" value={data.pin.region_name} />
        <PinInfoRow
          title="شناسه تراکنش"
          value={
            data.order?.code ? (
              <button
                type="button"
                className="flex items-center gap-1 text-body-sm-emphasis underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange"
                onClick={() => setIsTransactionOpen(true)}
              >
                <span>{data.order.code}</span>
                <LeftOutlined aria-hidden className="text-body-lg" />
              </button>
            ) : (
              ''
            )
          }
          hasBorder={false}
        />
      </section>
      <Divider className="bg-gray-02" size={8} />
      {data.pin.state === 'done' ? (
        <PackageSummary statistics={data.pin.statistics} />
      ) : (
        <Empty illustration={stateEmptyState} className="min-h-60 gap-2.5 px-10 text-center">
          <p className="text-body-sm-emphasis text-neutral-fg">اطلاعاتی برای نمایش وجود ندارد</p>
          <p className="text-body-sm text-gray-07">
            گزارش عملکرد بسته بعد از پایان بسته نمایش داده می‌شود.
          </p>
        </Empty>
      )}
      <BottomSheet
        show={isTransactionOpen}
        onClose={() => setIsTransactionOpen(false)}
        header={
          <div className="flex items-center gap-2 px-2 pt-2">
            <IconButton aria-label="بستن" size="md" onClick={() => setIsTransactionOpen(false)}>
              <CloseOutlined aria-hidden />
            </IconButton>
            <h2 className="text-subtitle-md text-neutral-fg">جزئیات تراکنش</h2>
          </div>
        }
      >
        <TransactionDetails data={data.order} />
      </BottomSheet>
    </PageLayout>
  );
};

export default PinDetails;
